import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { analytics } from '@/lib/analytics';

const DEFAULT_RADIUS = parseInt(import.meta.env.VITE_DEFAULT_RADIUS) || 8045;

// Location source types
export type LocationSource = 'gps' | 'city' | 'custom';

// Location status for GPS mode
export type LocationStatus = 'loading' | 'granted' | 'denied' | 'unavailable';

// Radius limits (in meters)
export const MIN_RADIUS = 805;   // 0.5 miles
export const MAX_RADIUS = 40234; // 25 miles

// Get today's date in YYYY-MM-DD format (local timezone)
// Between midnight and 3am, returns the previous calendar day
function getTodayDate(): string {
  const now = new Date();
  now.setHours(now.getHours() - 3);
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Returns true if the current time is between midnight and 3am
export function isLateNight(): boolean {
  return new Date().getHours() < 3;
}

// Get day-of-week index (0=Sunday) accounting for 3am rollover
export function getEffectiveDayIndex(): number {
  const now = new Date();
  now.setHours(now.getHours() - 3);
  return now.getDay();
}

// Add days to a date string (local timezone)
function addDays(dateStr: string, days: number): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  const newYear = date.getFullYear();
  const newMonth = String(date.getMonth() + 1).padStart(2, '0');
  const newDay = String(date.getDate()).padStart(2, '0');
  return `${newYear}-${newMonth}-${newDay}`;
}

// Format date for display: "Today", "Tomorrow", day of week (2-6 days), or "Jan 15" (7+ days)
export function formatDateDisplay(dateStr: string, today: string, lateNight?: boolean): string {
  if (dateStr === today) return lateNight ? '\u201CToday\u201D' : 'Today';
  if (dateStr === addDays(today, 1)) return 'Tomorrow';

  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  // Calculate days difference
  const [todayYear, todayMonth, todayDay] = today.split('-').map(Number);
  const todayDate = new Date(todayYear, todayMonth - 1, todayDay);
  const diffTime = date.getTime() - todayDate.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  // If less than 7 days away, show day of week
  if (diffDays >= 2 && diffDays < 7) {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return dayNames[date.getDay()];
  }

  // 7+ days away, show month and day
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${monthNames[date.getMonth()]} ${date.getDate()}`;
}

// Convert meters to miles for display
export function metersToMiles(meters: number): number {
  return Math.round((meters / 1609) * 10) / 10;
}

// Convert miles to meters
export function milesToMeters(miles: number): number {
  return Math.round(miles * 1609);
}

interface FilterContextValue {
  // Date
  date: string;
  today: string;
  isLateNight: boolean;
  setDate: (date: string) => void;
  goToPrevDay: () => void;
  goToNextDay: () => void;
  canGoBack: boolean;

  // Location
  lat: number | null;
  lng: number | null;
  locationLoading: boolean;
  locationStatus: LocationStatus;
  hasValidLocation: boolean;
  locationSource: LocationSource;
  locationLabel: string | null;
  setLocation: (lat: number, lng: number, source?: LocationSource, label?: string) => void;
  resetToGps: () => void;

  // Radius
  radius: number;
  setRadius: (radius: number) => void;
}

const FilterContext = createContext<FilterContextValue | null>(null);

export function FilterProvider({ children }: { children: ReactNode }) {
  const today = getTodayDate();
  const lateNight = isLateNight();
  const [date, setDateState] = useState(today);
  const [radius, setRadiusState] = useState(DEFAULT_RADIUS);

  // Location state - lat/lng null until real coords obtained
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [locationStatus, setLocationStatus] = useState<LocationStatus>('loading');
  const [locationSource, setLocationSource] = useState<LocationSource>('gps');
  const [locationLabel, setLocationLabel] = useState<string | null>(null);
  // Store GPS coordinates so we can restore them
  const [gpsLat, setGpsLat] = useState<number | null>(null);
  const [gpsLng, setGpsLng] = useState<number | null>(null);

  // Computed: do we have a usable location?
  const hasValidLocation = useMemo(() => {
    if (locationSource !== 'gps') return lat !== null && lng !== null;
    return locationStatus === 'granted' && lat !== null && lng !== null;
  }, [locationSource, locationStatus, lat, lng]);

  // Backward compat alias
  const locationLoading = locationStatus === 'loading';

  // Request geolocation on mount
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationStatus('unavailable');
      return;
    }

    setLocationStatus('loading');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLat = position.coords.latitude;
        const newLng = position.coords.longitude;
        setGpsLat(newLat);
        setGpsLng(newLng);
        // Only update current location if we're still in GPS mode
        if (locationSource === 'gps') {
          setLat(newLat);
          setLng(newLng);
        }
        setLocationStatus('granted');
      },
      (error) => {
        console.warn('Geolocation failed:', error.message);
        if (error.code === error.PERMISSION_DENIED) {
          setLocationStatus('denied');
        } else {
          setLocationStatus('unavailable');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  }, [locationSource]);

  const setDate = useCallback((newDate: string) => {
    // Don't allow past dates
    if (newDate >= today) {
      setDateState(newDate);
    }
  }, [today]);

  const goToPrevDay = useCallback(() => {
    const newDate = addDays(date, -1);
    if (newDate >= today) {
      setDateState(newDate);
    }
  }, [date, today]);

  const goToNextDay = useCallback(() => {
    setDateState(addDays(date, 1));
  }, [date]);

  const setRadius = useCallback((newRadius: number) => {
    // Clamp to allowed range
    const clamped = Math.min(Math.max(newRadius, MIN_RADIUS), MAX_RADIUS);
    setRadiusState(clamped);
  }, []);

  const setLocation = useCallback((newLat: number, newLng: number, source: LocationSource = 'gps', label?: string) => {
    setLat(newLat);
    setLng(newLng);
    setLocationSource(source);
    setLocationLabel(label || null);
    if (source !== 'gps') {
      setLocationStatus('granted');
    }

    // Track city selection
    if (source === 'city' && label) {
      analytics.selectCity(label, '');
      analytics.setUserCity(label);
    }
  }, []);

  const resetToGps = useCallback(() => {
    setLocationSource('gps');
    setLocationLabel(null);

    // Track GPS usage
    analytics.useGps();
    analytics.setUserCity(null);

    // Use stored GPS coordinates if available, otherwise request again
    if (gpsLat !== null && gpsLng !== null) {
      setLat(gpsLat);
      setLng(gpsLng);
      setLocationStatus('granted');
    } else if (navigator.geolocation) {
      setLat(null);
      setLng(null);
      setLocationStatus('loading');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLat(position.coords.latitude);
          setLng(position.coords.longitude);
          setGpsLat(position.coords.latitude);
          setGpsLng(position.coords.longitude);
          setLocationStatus('granted');
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setLocationStatus('denied');
          } else {
            setLocationStatus('unavailable');
          }
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setLocationStatus('unavailable');
    }
  }, [gpsLat, gpsLng]);

  const canGoBack = date > today;

  return (
    <FilterContext.Provider
      value={{
        date,
        today,
        isLateNight: lateNight,
        setDate,
        goToPrevDay,
        goToNextDay,
        canGoBack,
        lat,
        lng,
        locationLoading,
        locationStatus,
        hasValidLocation,
        locationSource,
        locationLabel,
        setLocation,
        resetToGps,
        radius,
        setRadius,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
}
